import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, X, Upload, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBanners = ({ token }) => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    titulo: '',
    link: '',
    categoria_id: '',
    ordem: 0,
    imagem_base64: ''
  });

  useEffect(() => {
    fetchBanners();
    fetchCategories();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/banners`);
      setBanners(response.data);
    } catch (error) {
      console.error('Erro ao carregar banners:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API}/banners/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        imagem_base64: response.data.imagem_base64
      }));
      toast.success('Imagem carregada!');
    } catch (error) {
      toast.error('Erro ao fazer upload da imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.imagem_base64) {
      toast.error('Por favor, faça upload de uma imagem');
      return;
    }

    try {
      await axios.post(
        `${API}/banners?imagem_base64=${encodeURIComponent(formData.imagem_base64)}`,
        {
          titulo: formData.titulo,
          link: formData.link || '',
          categoria_id: formData.categoria_id || null,
          ordem: formData.ordem
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Banner criado!');

      setShowForm(false);
      setFormData({ titulo: '', link: '', categoria_id: '', ordem: 0, imagem_base64: '' });
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar banner');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Deseja realmente excluir este banner?')) return;
    try {
      await axios.delete(`${API}/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner excluído!');
      fetchBanners();
    } catch (error) {
      toast.error('Erro ao excluir banner');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.nome : 'Todos';
  };

  return (
    <div>
      {!showForm ? (
        <>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Banners</h2>
            <Button onClick={() => setShowForm(true)} data-testid="add-banner-button" style={{ alignSelf: 'flex-start' }}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Banner
            </Button>
          </div>

          {banners.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              backgroundColor: '#f9f9f9', 
              borderRadius: '8px',
              color: '#666'
            }}>
              <ImageIcon size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>Nenhum banner cadastrado</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Clique em "Novo Banner" para adicionar
              </p>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
              gap: '1rem' 
            }}>
              {banners.map(banner => (
                <div 
                  key={banner.id} 
                  style={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  data-testid={`banner-card-${banner.id}`}
                >
                  <div style={{ aspectRatio: '16/9', overflow: 'hidden' }}>
                    <img 
                      src={banner.imagem_base64} 
                      alt={banner.titulo}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <h3 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>{banner.titulo}</h3>
                    <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.25rem' }}>
                      Ordem: {banner.ordem}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                      Categoria: {getCategoryName(banner.categoria_id)}
                    </p>
                    {banner.link && (
                      <p style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', wordBreak: 'break-all' }}>
                        Link: {banner.link}
                      </p>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(banner.id)}
                      style={{ color: '#ef4444', width: '100%', marginTop: '0.5rem' }}
                      data-testid={`delete-banner-${banner.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ 
          backgroundColor: '#fff', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Novo Banner</h3>
            <Button variant="ghost" onClick={() => { setShowForm(false); setFormData({ titulo: '', link: '', categoria_id: '', ordem: 0, imagem_base64: '' }); }}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <Label>Imagem do Banner *</Label>
              <div 
                style={{ 
                  marginTop: '0.5rem', 
                  border: '2px dashed #ddd', 
                  borderRadius: '8px', 
                  padding: '1.5rem', 
                  textAlign: 'center', 
                  cursor: 'pointer',
                  backgroundColor: formData.imagem_base64 ? '#f9f9f9' : '#fff'
                }} 
                onClick={() => document.getElementById('banner-image-upload').click()}
              >
                {formData.imagem_base64 ? (
                  <div>
                    <img 
                      src={formData.imagem_base64} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain',
                        borderRadius: '4px',
                        margin: '0 auto'
                      }} 
                    />
                    <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
                      Clique para trocar a imagem
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload size={36} style={{ margin: '0 auto', color: '#999' }} />
                    <p style={{ marginTop: '0.75rem', color: '#666', fontSize: '0.875rem' }}>
                      Clique para fazer upload
                    </p>
                    <p style={{ fontSize: '0.75rem', color: '#999' }}>Recomendado: 1920x600px</p>
                  </>
                )}
                <input 
                  id="banner-image-upload" 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                  style={{ display: 'none' }} 
                  disabled={uploading}
                  data-testid="banner-image-input"
                />
              </div>
              {uploading && (
                <p style={{ textAlign: 'center', marginTop: '0.5rem', color: '#666', fontSize: '0.875rem' }}>
                  Fazendo upload...
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="banner-titulo">Título *</Label>
              <Input
                id="banner-titulo"
                data-testid="banner-titulo-input"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ex: Promoção de Verão"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <Label htmlFor="banner-categoria">Categoria (opcional)</Label>
                <select
                  id="banner-categoria"
                  data-testid="banner-categoria-select"
                  value={formData.categoria_id}
                  onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">Exibir em todas</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="banner-ordem">Ordem</Label>
                <Input
                  id="banner-ordem"
                  type="number"
                  data-testid="banner-ordem-input"
                  value={formData.ordem}
                  onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="banner-link">Link (opcional)</Label>
              <Input
                id="banner-link"
                type="url"
                data-testid="banner-link-input"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                placeholder="https://..."
              />
              <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                URL para redirecionar quando clicar no banner
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { 
                  setShowForm(false); 
                  setFormData({ titulo: '', link: '', categoria_id: '', ordem: 0, imagem_base64: '' }); 
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={uploading} data-testid="save-banner-button">
                Criar Banner
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
